// components/admin/quotes/QuoteFormModal.jsx
import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { quotesAPI } from '../../../api/quotes';

export default function QuoteFormModal({ mode, quote, onClose, onSuccess, onError }) {
  const isEdit = mode === 'edit';
  
  const [formData, setFormData] = useState({
    clientName: isEdit ? quote.clientName : '',
    clientEmail: isEdit ? quote.clientEmail : '',
    phone: isEdit ? quote.phone : '',
    company: isEdit ? quote.company || '' : '',
    projectType: isEdit ? quote.projectType : '',
    projectDescription: isEdit ? quote.projectDescription : '',
    budgetRange: isEdit ? quote.budgetRange || 'À définir' : 'À définir',
    deadline: isEdit && quote.deadline ? quote.deadline.split('T')[0] : '',
    features: isEdit ? quote.features || [] : [],
    additionalNotes: isEdit ? quote.additionalNotes || '' : '',
    status: isEdit ? quote.status : 'pending'
  });
  
  const [newFeature, setNewFeature] = useState('');
  const [loading, setLoading] = useState(false);
  
  const projectTypes = [
    'Site Web',
    'Application Mobile',
    'E-commerce',
    'Refonte',
    'Maintenance',
    'Autre'
  ];
  
  const budgetRanges = [
    'Moins de 1 000€',
    '1 000€ - 3 000€',
    '3 000€ - 5 000€',
    '5 000€ - 10 000€',
    '10 000€ - 20 000€',
    'Plus de 20 000€',
    'À définir'
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isEdit) {
        await quotesAPI.update(quote._id, formData);
      } else {
        await quotesAPI.create(formData);
      }
      
      onSuccess();
      
    } catch (err) {
      onError(err.message || 'Erreur lors de l\'enregistrement');
      
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };
  
  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Modifier le devis' : 'Créer un nouveau devis'}</h3>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <div className="form-grid">
              {/* Informations client */}
              <div className="form-section">
                <h4>Informations client</h4>
                <div className="form-group">
                  <label>Nom complet *</label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({...formData, clientEmail: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Entreprise</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                  />
                </div>
              </div>
              
              {/* Détails du projet */}
              <div className="form-section">
                <h4>Détails du projet</h4>
                <div className="form-group">
                  <label>Type de projet *</label>
                  <select
                    value={formData.projectType}
                    onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                    required
                  >
                    <option value="">Sélectionnez...</option>
                    {projectTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Budget estimé</label>
                  <select
                    value={formData.budgetRange}
                    onChange={(e) => setFormData({...formData, budgetRange: e.target.value})}
                  >
                    {budgetRanges.map(range => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date souhaitée</label>
                  <input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="pending">En attente</option>
                    <option value="sent">Envoyé</option>
                    <option value="accepted">Accepté</option>
                    <option value="declined">Refusé</option>
                    <option value="expired">Expiré</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Description */}
            <div className="form-group full-width">
              <label>Description du projet *</label>
              <textarea
                value={formData.projectDescription}
                onChange={(e) => setFormData({...formData, projectDescription: e.target.value})}
                rows="4"
                required
              />
            </div>
            
            {/* Fonctionnalités */}
            <div className="form-section">
              <h4>Fonctionnalités</h4>
              <div className="features-input">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="Ajouter une fonctionnalité"
                />
                <button type="button" onClick={handleAddFeature} className="btn-small">
                  <Plus size={16} />
                </button>
              </div>
              <div className="features-list">
                {formData.features.map((feature, idx) => (
                  <span key={idx} className="feature-tag">
                    {feature}
                    <button 
                      type="button" 
                      onClick={() => handleRemoveFeature(idx)}
                      className="remove-feature"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
            
            {/* Notes supplémentaires */}
            <div className="form-group full-width">
              <label>Notes supplémentaires</label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})}
                rows="3"
              />
            </div>
          </div>
          
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Créer le devis')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}